import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Grid,
    Paper,
    Typography,
    CircularProgress,
    Alert,
    Button,
    IconButton,
    Breadcrumbs,
    Link
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    ArrowBack as ArrowBackIcon,
    FolderOpen as FolderIcon,
    Dashboard as DashboardIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import TEXT from './text';
import GroupOverview from './GroupOverview';
import RecordsDistributionChart from './RecordsDistributionChart';
import DailyGrowthChart from './DailyGrowthChart';
import * as calculationService from './services/calculationService';
import * as versionService from './services/versionService';
import * as indexedDBService from './services/indexedDBService';
import * as dataSyncService from './services/dataSyncService';
import {Divider} from "@mui/material";

const DbGroupDashboard = ({ groupId, groupName, onBack }) => {
    const language = useSelector(state => state.App_Settings.language);
    
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [error, setError] = useState(null);
    const [dashboardData, setDashboardData] = useState({
        overview: null,
        version: 0,
        lastUpdated: null
    });
    const [refreshing, setRefreshing] = useState(false);

    // Initialize IndexedDB stores on mount
    useEffect(() => {
        const initStores = async () => {
            try {
                await indexedDBService.initDashboardStores();
                console.log('[Dashboard] IndexedDB stores initialized');
            } catch (error) {
                console.error('[Dashboard] Error initializing stores:', error);
            }
        };
        
        initStores();
    }, []);

    // Load dashboard data
    useEffect(() => {
        if (groupId) {
            loadDashboardData();
        }
    }, [groupId]);

    const loadDashboardData = async () => {
        setLoading(true);
        setError(null);
        
        try {
            // Step 1: Check if data exists in IndexedDB
            const hasData = await dataSyncService.hasGroupData(groupId);
            
            // Step 2: If no data, sync from server
            if (!hasData) {
                console.log('[Dashboard] No local data found, syncing from server...');
                setSyncing(true);
                const syncResult = await dataSyncService.syncGroupData(groupId, groupName);
                setSyncing(false);
                
                if (!syncResult.success) {
                    throw new Error(syncResult.error || 'Failed to sync data');
                }
                
                console.log(`[Dashboard] Synced ${syncResult.recordCount} records`);
            }
            
            // Step 3: Check version
            const versionCheck = await versionService.checkVersionChange(groupId);
            
            // Step 4: Load overview data
            const overview = await calculationService.getDashboardData(
                groupId,
                'overview',
                { storeName: `group_${groupId}_data` }
            );
            
            setDashboardData({
                overview,
                version: versionCheck.currentVersion,
                lastUpdated: new Date()
            });
            
        } catch (err) {
            console.error('[Dashboard] Error loading data:', err);
            setError(err.message || TEXT.ErrorLoadingData[language]);
        } finally {
            setLoading(false);
            setSyncing(false);
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        setSyncing(true);
        try {
            // Re-sync data from server
            const syncResult = await dataSyncService.syncGroupData(groupId, groupName);
            
            if (!syncResult.success) {
                throw new Error(syncResult.error || 'Failed to sync data');
            }
            
            // Invalidate cache and reload
            await calculationService.invalidateGroupCalculations(groupId);
            await loadDashboardData();
        } catch (err) {
            console.error('[Dashboard] Error refreshing:', err);
            setError(err.message || TEXT.ErrorLoadingData[language]);
        } finally {
            setRefreshing(false);
            setSyncing(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        {syncing ? TEXT.SyncingData?.[language] || 'Syncing data...' : TEXT.Loading[language]}
                    </Typography>
                </Box>
            </Box>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4 }}>
                <Alert 
                    severity="error" 
                    action={
                        <Button color="inherit" size="small" onClick={loadDashboardData}>
                            {TEXT.Retry[language]}
                        </Button>
                    }
                >
                    {error}
                </Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth sx={{ mt: 2, mb: 4 }}>
            {/* Header with breadcrumbs - only show if onBack is provided */}
            {onBack && (
                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={onBack} size="small">
                        <ArrowBackIcon />
                    </IconButton>
                    <Breadcrumbs>
                        <Link 
                            component="button" 
                            variant="body1" 
                            onClick={onBack}
                            sx={{ textDecoration: 'none', cursor: 'pointer' }}
                        >
                            {TEXT.BackToGroups[language]}
                        </Link>
                        <Typography color="text.primary" variant="body1">
                            {groupName || `Group ${groupId}`}
                        </Typography>
                    </Breadcrumbs>
                </Box>
            )}

            <style>
                {`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}
            </style>

            {/* Dashboard Content - Single Paper */}
            <Paper sx={{ p: { xs: 2, md: 3 } }} maxWidth>
                <Grid container spacing={3}>
                    {/* Title with Version and Refresh */}
                    <Grid item container  xs={12} md={12} lg={12} alignItems="center" size={12}>
                        <Grid item xs={10} lg={10} md={10}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <FolderIcon />
                                    {groupName || `Group ${groupId}`}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={2} md={2} lg={2} >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                    {TEXT.Version[language]}: {dashboardData.version}
                                </Typography>
                                <IconButton
                                    onClick={handleRefresh}
                                    disabled={refreshing}
                                    size="small"
                                    title={TEXT.RefreshData[language]}
                                >
                                    <RefreshIcon sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
                                </IconButton>
                            </Box>
                        </Grid>
                        <Grid item lg={12}>
                            <Divider sx={{ my: 1 }} />
                        </Grid>
                    </Grid> 

                    {/* Records Distribution Chart */}
                    <Grid item xs={12} lg={6}>
                        <RecordsDistributionChart
                            groupId={groupId}
                            storeName={`group_${groupId}_data`}
                        />
                    </Grid>

                    {/* Daily Growth Chart */}
                    <Grid item xs={12} lg={6}>
                        <DailyGrowthChart
                            groupId={groupId}
                            storeName={`group_${groupId}_data`}
                        />
                    </Grid>
                    {/* Group Overview */}
                    <Grid item xs={12}>
                        <GroupOverview
                            groupId={groupId}
                            groupName={groupName}
                            overview={dashboardData.overview}
                        />
                    </Grid>


                </Grid>
            </Paper>

            {/* Last Updated Info */}
            {dashboardData.lastUpdated && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                        {TEXT.LastUpdated[language]}: {dashboardData.lastUpdated.toLocaleTimeString()}
                    </Typography>
                </Box>
            )}
        </Container>
    );
};

export default DbGroupDashboard;
